import { useState, useEffect } from "react";
import axios from "axios";
import { Sparkles, Salad, BookOpen, Tag } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

const TYPE_META = {
  feature: { label: "おすすめ商品の特集", icon: Sparkles, chip: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  nutrition: { label: "お役立ち情報", icon: Salad, chip: "bg-sky-50 text-sky-700 border-sky-200" },
};

function ArticleBody({ text }) {
  // 【見出し】は太字見出しに、空行で段落分け
  const blocks = (text || "").split(/\n\s*\n/);
  return (
    <div className="space-y-3">
      {blocks.map((b, i) => {
        const trimmed = b.trim();
        const headingMatch = trimmed.match(/^【(.+?)】\s*([\s\S]*)$/);
        if (headingMatch) {
          return (
            <div key={i}>
              <h3 className="text-base font-bold text-slate-800 flex items-center gap-1.5 mb-1">
                <span className="w-1 h-4 bg-emerald-500 rounded-full inline-block" />
                {headingMatch[1]}
              </h3>
              {headingMatch[2] && <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-wrap">{headingMatch[2]}</p>}
            </div>
          );
        }
        return <p key={i} className="text-sm text-slate-600 leading-relaxed whitespace-pre-wrap">{trimmed}</p>;
      })}
    </div>
  );
}

export default function ArticlesPage() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/articles`, getAuthHeaders());
        setArticles(res.data || []);
      } catch (e) {
        setArticles([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = filter === "all" ? articles : articles.filter((a) => a.type === filter);

  return (
    <div className="space-y-6" data-testid="store-articles-page">
      <div>
        <div className="flex items-center gap-2">
          <div className="w-1.5 h-7 bg-emerald-600 rounded-full" />
          <h1 className="text-2xl font-bold text-slate-800">特集・お役立ち情報</h1>
        </div>
        <p className="text-sm text-slate-500 mt-1">旬のおすすめ食材の特集や、給食づくりに役立つ情報をお届けします。</p>
      </div>

      <div className="flex gap-2 flex-wrap">
        {[
          { key: "all", label: "すべて" },
          { key: "feature", label: "おすすめ特集" },
          { key: "nutrition", label: "お役立ち情報" },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setFilter(t.key)}
            data-testid={`store-article-filter-${t.key}`}
            className={`px-3.5 py-1.5 rounded-full border text-sm transition-all ${
              filter === t.key ? "bg-emerald-600 border-emerald-600 text-white" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-slate-400 text-sm py-10 text-center">読み込み中...</div>
      ) : filtered.length === 0 ? (
        <div className="text-slate-400 text-sm py-16 text-center border border-dashed border-slate-200 rounded-xl" data-testid="store-articles-empty">
          <BookOpen className="h-8 w-8 mx-auto mb-2 text-slate-300" />
          現在お知らせできる記事はありません。
        </div>
      ) : (
        <div className="grid gap-5 md:grid-cols-2">
          {filtered.map((a) => {
            const meta = TYPE_META[a.type] || TYPE_META.feature;
            const Icon = meta.icon;
            return (
              <article
                key={a.id}
                data-testid={`store-article-${a.id}`}
                className="bg-white border border-slate-200 rounded-2xl overflow-hidden flex flex-col"
              >
                {a.cover_image_url ? (
                  <img src={`${BACKEND_URL}${a.cover_image_url}`} alt="" className="w-full h-44 object-cover bg-slate-100" />
                ) : (
                  <div className="w-full h-28 bg-gradient-to-br from-emerald-100 to-emerald-50 flex items-center justify-center">
                    <Icon className="h-9 w-9 text-emerald-300" />
                  </div>
                )}
                <div className="p-5 flex-1 flex flex-col">
                  <span className={`self-start text-[11px] px-2 py-0.5 rounded-full border ${meta.chip} flex items-center gap-1`}>
                    <Icon className="h-3 w-3" />
                    {meta.label}
                  </span>
                  <h2 className="text-lg font-bold text-slate-800 mt-2">{a.title}</h2>
                  <div className="mt-2 flex-1">
                    <ArticleBody text={a.body} />
                  </div>

                  {(a.products || []).length > 0 && (
                    <div className="mt-4 pt-4 border-t border-slate-100">
                      <div className="text-xs font-semibold text-slate-500 mb-2 flex items-center gap-1">
                        <Tag className="h-3.5 w-3.5 text-emerald-500" />おすすめ商品
                      </div>
                      <div className="grid gap-2 sm:grid-cols-2">
                        {a.products.map((p) => (
                          <div key={p.id} data-testid={`store-article-product-${p.id}`} className="flex items-center gap-2 bg-slate-50 border border-slate-100 rounded-lg p-2">
                            {p.image_url ? (
                              <img src={`${BACKEND_URL}${p.image_url}`} alt="" className="w-10 h-10 rounded object-cover bg-white" />
                            ) : (
                              <div className="w-10 h-10 rounded bg-emerald-100 flex items-center justify-center shrink-0">
                                <Sparkles className="h-4 w-4 text-emerald-400" />
                              </div>
                            )}
                            <div className="min-w-0">
                              <div className="text-sm text-slate-700 truncate">{p.name}</div>
                              <div className="text-xs text-slate-400">
                                {p.price != null ? `¥${p.price} / ${p.unit || ""}` : p.unit || ""}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
